import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import { Dropdown, DropdownItemProps, Form } from "semantic-ui-react";
import { validators } from "not-valid";

import { EditComponent, SaveDispatchProps, EditProps as SharedEditProps } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";

import { getProfessionDropdownOptions } from "@teamprevent/common/adminUser/models";

import { AdminUserEditModel, AppState } from "../model";
import { adminUserSelector, basePathSelector } from "../selectors";
import { saveAdminUser } from "../actions";
import { ADMIN_ROLES } from "../../../auth/model";
import { DaySelector } from "../../../global/DaySelector";

import { EditModal } from "./EditModal";

import { push } from "redux-little-router";

export interface EditProps extends SharedEditProps<AdminUserEditModel> {
    open: boolean;
}

export interface DispatchProps extends SaveDispatchProps<AdminUserEditModel> {
    close: () => void;
}

export class EditUnconnected extends EditComponent<AdminUserEditModel, EditProps> {
    private adminRoles: DropdownItemProps[];

    constructor(props: EditProps & DispatchProps) {
        super(props);

        this.adminRoles = (Object.keys(ADMIN_ROLES) as (keyof typeof ADMIN_ROLES)[])
            .map(role => ({ key: ADMIN_ROLES[role].name, text: ADMIN_ROLES[role].displayName, value: ADMIN_ROLES[role].name }));
    }

    private onProfessionChange = (event: React.SyntheticEvent<HTMLElement>, { value }: any) => {
        this.updateProperty("profession", value, true);
    };

    public render() {
        return (
            <Form onSubmit={this.handleSubmit}>
                <Input.Email
                    value={this.state.values.emailAddress}
                    label="Email Address *"
                    showErrors={this.state.showErrors}
                    required
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("emailAddress", value, valid)}
                />
                <br />
                <Input.Checkbox
                    value={this.state.values.isLocumUser}
                    label="Locum User"
                    showErrors={this.state.showErrors}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("isLocumUser", value, valid)}
                />
                {this.state.values.isLocumUser &&
                    <Input.Text
                        value={this.state.values.locumUserFullName}
                        label="Locum Full Name *"
                        showErrors={this.state.showErrors}
                        required
                        // eslint-disable-next-line react/jsx-no-bind
                        onChange={(value, valid) => this.updateProperty("locumUserFullName", value, valid)}
                    />
                }
                <Input.PhoneNumber
                    value={this.state.values.mobileNumber}
                    label="Mobile Number *"
                    showErrors={this.state.showErrors}
                    required
                    validation={[validators.validPhoneNumber(10)]}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("mobileNumber", value, valid)}
                />
                <Input.DropdownMulti
                    value={this.state.values.roles}
                    label="Roles"
                    placeholder="Roles"
                    options={this.adminRoles}
                    showErrors={this.state.showErrors}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("roles", value, valid)}
                />
                <br />
                <Input.Checkbox
                    value={this.state.values.reportQA}
                    label="Report QA"
                    showErrors={this.state.showErrors}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("reportQA", value, valid)}
                />
                <Form.Field>
                    <label>Profession</label>
                    <Dropdown
                        value={this.state.values.profession}
                        placeholder="Profession"
                        options={getProfessionDropdownOptions()}
                        error={this.state.showErrors}
                        onChange={this.onProfessionChange}
                        clearable={true}
                        fluid={true}
                        selection={true}
                    />
                </Form.Field>
                <Input.Text
                    value={this.state.values.qualification}
                    label="Qualification"
                    showErrors={this.state.showErrors}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("qualification", value, valid)}
                />
                <Input.Time
                    value={this.state.values.workingDayStartTime}
                    label="Start Time"
                    required
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("workingDayStartTime", value, valid)}
                />
                <Input.Time
                    value={this.state.values.workingDayEndTime}
                    label="Finish Time"
                    required
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("workingDayEndTime", value, valid)}
                />
                <DaySelector
                    selectedDays={this.state.values.workingDays}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={days => this.updateProperty("workingDays", days, true)}
                />
            </Form>
        );
    }

    public submit(): void {
        this.handleSubmit({ preventDefault: (): void => undefined } as any);
    }
}

function mapStateToProps(state: AppState) {
    return {
        model: adminUserSelector(state) as AdminUserEditModel,
        open: state.router.pathname?.endsWith("/edit"),
        basePath: basePathSelector(state)
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<AppState, undefined, any>) {
    return {
        dispatchSave: (adminUser: AdminUserEditModel, basePath: string) => dispatch(saveAdminUser(adminUser, basePath)),
        dispatchClose: (basePath: string) => dispatch(push(basePath, {}))
    };
}

function mergeProps(propsFromState: any, propsFromDispatch: any): EditProps & DispatchProps {
    return {
        model: propsFromState.model,
        open: propsFromState.open,
        save: (adminUser: AdminUserEditModel) => propsFromDispatch.dispatchSave(adminUser, propsFromState.basePath),
        close: () => propsFromDispatch.dispatchClose(`${propsFromState.basePath}/${propsFromState.model.id}`)
    };
}

export const Edit = connect(mapStateToProps, mapDispatchToProps, mergeProps)(EditModal);
